// Shared header for standalone pages — marketplace, listings, payouts, and
// anything else that lives outside the dashboard shell.
//
// These pages were each rolling their own back-link and title, so a visitor who
// landed on one from a shared URL had no way to reach the rest of the product
// and no sense of where they were. That is fine for a settings page reached from
// inside the app; it is not fine for a storefront, which is a page people arrive
// at cold.
//
// The frosted nav matches the landing page rather than the dashboard sidebar,
// because these pages are closer in character to the marketing surface: someone
// may well see the marketplace before they ever see the builder.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import './PageHeader.css';

/** Links for inner pages that don't have their own set. */
export const SITE_LINKS = [
  { label: 'Marketplace', to: '/marketplace' },
  { label: 'Experiences', to: '/experiences' },
  { label: 'Developers', to: '/developer' },
  { label: 'Docs', to: '/docs' },
];

export interface PageHeaderProps {
  /** Large title in the hero band. Omit for a bare nav with no hero. */
  title?: string;
  /** Second headline line, dimmed and ending in the landing page's cursor. */
  accent?: string;
  /** 'tall' gives the hero art room to show (storefront pages). */
  size?: 'normal' | 'tall';
  subtitle?: string;
  /** Background image for the hero band. Sits behind text at low opacity. */
  hero?: string;
  /** Right-hand actions inside the hero, under the subtitle. */
  actions?: React.ReactNode;
  /** Page-specific links shown in the nav bar. */
  links?: { label: string; to: string }[];
  /** Extra buttons at the right of the nav bar, before Dashboard / Sign in. */
  navExtra?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  accent,
  size = 'normal',
  subtitle,
  hero,
  actions,
  links = SITE_LINKS,
  navExtra,
}) => {
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSignedIn(!!session));
  }, []);

  return (
    <div className={`ph-wrap ${hero ? 'has-art' : ''} ${size === 'tall' ? 'ph-tall' : ''}`}>
      {/* The hero art sits behind everything at low opacity and fades out
          downward, so text contrast never depends on which part of the image
          happens to be underneath it. */}
      {hero && (
        <>
          <div className="ph-hero-bg" style={{ backgroundImage: `url(${hero})` }} />
          <div className="ph-hero-veil" />
        </>
      )}

      <div className="ph-inner ph-container">
        <nav className="ph-nav">
          <Link to="/" className="ph-logo">
            Gathr<span>XR</span>
          </Link>

          <div className="ph-links">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="ph-link">{l.label}</Link>
            ))}
          </div>

          <div className="ph-actions">
            {navExtra}
            {/* Rendered only once the session is known. Flashing "Sign in" at a
                signed-in user is a small thing that makes a product feel
                unreliable. */}
            {signedIn === true && (
              <button className="ph-btn" onClick={() => navigate('/dashboard')}>
                Dashboard
              </button>
            )}
            {signedIn === false && (
              <button className="ph-btn ph-btn-primary" onClick={() => navigate('/')}>
                Sign in
              </button>
            )}
          </div>
        </nav>

        {title && (
          <header className="ph-hero">
            <h1 className="ph-title">
              {title}
              {accent && <><br /><span className="ph-title-accent">{accent}</span></>}
              {!accent && <span className="ph-cursor" aria-hidden />}
            </h1>
            {subtitle && <p>{subtitle}</p>}
            {actions && <div className="ph-hero-actions">{actions}</div>}
          </header>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
